import { useTranslation } from '@silospot/i18n/react';
import type { VisitFilter, VisitRowDto } from '@silospot/shared';
import { toast } from '@silospot/ui';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  Bot,
  Cloud,
  Download,
  FileText,
  Monitor,
  MonitorSmartphone,
  Phone,
  ShieldBan,
  ShieldCheck,
  Smartphone,
  Tablet,
  TriangleAlert,
} from 'lucide-react';
import { type ReactElement, type ReactNode, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Badge,
  Btn,
  Card,
  CardHead,
  Chip,
  type Column,
  DataTable,
  Hint,
  PageHeader,
} from '../../components/cld';
import { BlocklistModal, type BlocklistPrefill } from '../../components/cld/blocklist-modal';
import { Donut } from '../../components/cld/charts';
import { RuleAdoptionPanel } from '../../components/cld/rule-adoption-panel';
import { Flag } from '../../components/location';
import { api } from '../../lib/api';
import { fmtCompact, fmtCsvStamp, fmtDateTimeFull, parseUtc } from '../../lib/datetime';
import { usePeriodRange } from '../../stores/period-store';

const FILTERS: { key: VisitFilter; label: string }[] = [
  { key: 'all', label: 'filterAll' },
  { key: 'ads', label: 'filterAds' },
  { key: 'organic', label: 'filterOrganic' },
  { key: 'suspect', label: 'filterSuspect' },
  { key: 'blocked', label: 'filterBlocked' },
];

const CONN_COLOR: Record<string, string> = {
  residential: '#16a34a',
  datacenter: '#d97706',
  proxy_vpn: '#0891b2',
  tor: '#475569',
  mobile: '#2563eb',
};

const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
function ago(iso: string): string {
  const min = Math.round((Date.now() - parseUtc(iso).getTime()) / 60000);
  if (Math.abs(min) < 60) return rtf.format(-min, 'minute');
  const h = Math.round(min / 60);
  if (Math.abs(h) < 24) return rtf.format(-h, 'hour');
  return rtf.format(-Math.round(h / 24), 'day');
}

/** Session length ms → compact "Xs" / "Xm Ys" (— when unknown). */
function fmtDuration(ms: number): string {
  if (!ms || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem ? `${m}m ${rem}s` : `${m}m`;
}

/** Device-type icon (from the collected device_type; never inferred otherwise). */
const DEVICE_ICON: Record<string, typeof Monitor> = {
  desktop: Monitor,
  mobile: Smartphone,
  tablet: Tablet,
  bot: Bot,
};

/** Three risk zones — same thresholds and colours as the IP reputation card. */
const RISK3 = [
  { key: 'riskLow', color: '#16a34a', flex: 40 },
  { key: 'riskSuspect', color: '#d97706', flex: 30 },
  { key: 'riskHigh', color: '#dc2626', flex: 30 },
] as const;

/** 0 = Faible (0-39), 1 = Suspect (40-69), 2 = Élevé (70-100). */
function riskLevel(score: number): 0 | 1 | 2 {
  return score < 40 ? 0 : score < 70 ? 1 : 2;
}

/** Path + query of a landing URL (scheme/host dropped). */
function pathOf(url: string): string {
  if (!url) return '/';
  try {
    const u = new URL(url);
    return `${u.pathname}${u.search}` || '/';
  } catch {
    return url;
  }
}

const adsBadge =
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-[#fef3c7] px-2.5 py-0.5 text-[12.5px] font-semibold text-[#b45309]';

function csvCell(v: string | number | boolean): string {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function IpPage(): ReactElement {
  const { t } = useTranslation('pages');
  const { id = '' } = useParams();
  const range = usePeriodRange();
  const [filter, setFilter] = useState<VisitFilter>('all');
  const [bl, setBl] = useState<BlocklistPrefill | null>(null);
  // Exact-IP search (v4 or v6): draft in the input, applied on Enter/blur.
  const [ipDraft, setIpDraft] = useState('');
  const [ipSearch, setIpSearch] = useState('');

  const visitsQ = useInfiniteQuery({
    queryKey: ['cld-ip-visits', id, range, filter, ipSearch],
    queryFn: ({ pageParam }) =>
      api.getVisits(id, {
        ...range,
        filter,
        ip: ipSearch || undefined,
        limit: 50,
        cursor: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!id,
  });
  const summaryQ = useQuery({
    queryKey: ['cld-ip-summary', id, range],
    queryFn: () => api.getIpSummary(id, range),
    enabled: !!id,
  });
  // Range-level ad-click fraud — the window follows the selected period (today =
  // today's pools), so it is the primary fraud-health signal for that period.
  const rangeQ = useQuery({
    queryKey: ['cld-range-threats', id, range],
    queryFn: () => api.getRangeThreats(id, range),
    enabled: !!id,
  });
  const rangeData = rangeQ.data;
  const flagged16 = useMemo(
    () => new Set(rangeData?.ranges.map((r) => r.cidr16) ?? []),
    [rangeData],
  );
  const ip16 = (ip: string): string =>
    ip.includes('.') && !ip.includes(':') ? `${ip.split('.').slice(0, 2).join('.')}.0.0` : '';
  const rows = visitsQ.data?.pages.flatMap((p) => p.rows) ?? [];

  const exportCsv = () => {
    const head = [
      t('ip.colTime'),
      t('ip.colSource'),
      t('ip.colPageUtm'),
      'UTM',
      t('ip.colDeviceLang'),
      t('fraud.colIp'),
      t('geo.country'),
      t('geo.city'),
      t('ip.colClicks'),
      t('ip.colRecidive'),
      t('ip.colConversion'),
      t('ip.colRisk'),
    ];
    const lines = rows.map((r) =>
      [
        fmtCsvStamp(r.arrivedAt),
        r.gclid ? 'Google Ads' : r.utmSource || r.referrerDomain || r.sourceType,
        pathOf(r.landingPage),
        r.utmCampaign,
        `${r.browser}${r.headless ? ' (headless)' : ''} ${r.os} ${r.language}`.trim(),
        r.ip,
        r.country,
        r.city,
        r.clickEvents,
        r.recidivismIp,
        r.formSubmitted ? 'form' : r.phoneClicks > 0 ? 'phone' : '',
        r.fraudScore,
      ]
        .map(csvCell)
        .join(','),
    );
    const blob = new Blob([`${[head.join(','), ...lines].join('\n')}\n`], {
      type: 'text/csv;charset=utf-8',
    });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `silospot-visites-ip-${range.period}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(t('ip.exportDone'));
  };

  const sourceBadge = (r: VisitRowDto): ReactNode => {
    if (r.gclid) return <span className={adsBadge}>Google Ads</span>;
    const us = r.utmSource.toLowerCase();
    if (us.includes('meta') || us.includes('facebook') || us === 'fb')
      return <span className={adsBadge}>Meta Ads</span>;
    const label = r.referrerDomain || r.sourceType;
    return (
      <span title={label || undefined} className="block truncate text-[13px] text-text-2">
        {label || '—'}
      </span>
    );
  };

  const conversion = (r: VisitRowDto): ReactNode => {
    if (r.formSubmitted)
      return (
        <span className="inline-flex items-center gap-1 whitespace-nowrap text-[12.5px] font-semibold text-clean-fg">
          <FileText className="h-3 w-3 shrink-0" />
          {t('ip.convForm')}
        </span>
      );
    if (r.phoneClicks > 0)
      return (
        <span className="inline-flex items-center gap-1 whitespace-nowrap text-[12.5px] font-semibold text-clean-fg">
          <Phone className="h-3 w-3 shrink-0" />
          {t('ip.convPhone')}
        </span>
      );
    return <span className="text-text-3">—</span>;
  };

  // Compact 3-zone risk bar (green/orange/red) with a cursor + small level word.
  const riskBar = (r: VisitRowDto): ReactNode => {
    const s = Math.max(0, Math.min(100, r.fraudScore));
    const level = RISK3[riskLevel(r.fraudScore)];
    return (
      <div className="w-[72px]">
        <div className="relative">
          <div className="flex h-1.5 items-stretch gap-px">
            {RISK3.map((z) => (
              <span
                key={z.key}
                className="rounded-full"
                style={{ flex: z.flex, background: z.color }}
              />
            ))}
          </div>
          <span
            className="absolute -top-[3px] h-0 w-0 border-x-[3px] border-t-[4px] border-x-transparent border-t-text"
            style={{ left: `calc(${s}% - 3px)` }}
          />
        </div>
        <div
          className="mt-[2px] truncate text-center text-[11px] font-semibold leading-none"
          style={{ color: level.color }}
        >
          {t(`ip.${level.key}`)}
        </div>
      </div>
    );
  };

  const visitCols: Column<VisitRowDto>[] = [
    {
      key: 'arrivedAt',
      label: t('ip.colTime'),
      width: 'w-[104px]',
      sortValue: (r) => new Date(r.arrivedAt).getTime(),
      render: (r) => (
        <div title={fmtDateTimeFull(r.arrivedAt)}>
          <div className="whitespace-nowrap font-mono text-[13px] text-text">
            {fmtCompact(r.arrivedAt)}
          </div>
          <div className="truncate text-[11.5px] text-text-3">{ago(r.arrivedAt)}</div>
        </div>
      ),
    },
    {
      key: 'src',
      label: t('ip.colSource'),
      sortable: false,
      width: 'w-[108px]',
      render: sourceBadge,
    },
    {
      key: 'page',
      label: t('ip.colPageUtm'),
      sortable: false,
      width: 'w-[134px]',
      render: (r) => (
        <div className="min-w-0">
          <div title={r.landingPage} className="truncate font-mono text-[13px] text-text-2">
            {pathOf(r.landingPage)}
          </div>
          {r.utmCampaign ? (
            <div className="truncate text-[11.5px] text-accent-strong">utm={r.utmCampaign}</div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'device',
      label: t('ip.colDeviceLang'),
      sortable: false,
      width: 'w-[114px]',
      render: (r) => {
        const Icon = DEVICE_ICON[r.deviceType] ?? MonitorSmartphone;
        return (
          <div className="flex items-start gap-1.5">
            <Icon className="mt-px h-3.5 w-3.5 shrink-0 text-text-3" aria-label={r.deviceType} />
            <div className="min-w-0">
              <div className="flex items-center gap-1 text-[13px] text-text-2">
                <span className="truncate">{r.browser || '—'}</span>
                {r.headless ? (
                  <Hint text={t('ip.hintHeadless')}>
                    <TriangleAlert className="h-3 w-3 shrink-0 text-fraud-fg" />
                  </Hint>
                ) : null}
              </div>
              <div className="truncate font-mono text-[11.5px] text-text-3">
                {r.language || '—'}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'ip',
      label: t('fraud.colIp'),
      sortable: false,
      width: 'w-[124px]',
      render: (r) => (
        <div className="min-w-0">
          <button
            type="button"
            title={r.ip ? `${r.ip} — ${t('ip.clickToCopy')}` : undefined}
            disabled={!r.ip}
            onClick={(e) => {
              e.stopPropagation();
              void navigator.clipboard.writeText(r.ip);
              toast.success(t('ip.copied'));
            }}
            className="flex max-w-full items-center gap-1 font-mono text-text hover:text-primary"
          >
            <span className="min-w-0 truncate">{r.ip || '—'}</span>
            {r.blocked ? (
              <span className="shrink-0 text-[11px] font-semibold text-fraud-fg">●</span>
            ) : null}
          </button>
          {flagged16.has(ip16(r.ip)) ? (
            <span className="mt-0.5 inline-flex w-fit items-center gap-1 rounded-[3px] bg-[#fdeaea] px-1 text-[10px] font-semibold text-fraud-fg">
              <TriangleAlert className="h-2.5 w-2.5 shrink-0" />
              {t('range.tableBadge')}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'country',
      label: t('ip.colCountryCity'),
      sortable: false,
      width: 'w-[94px]',
      render: (r) => (
        <div className="min-w-0">
          <span className="flex items-center gap-1.5">
            <Flag country={r.country} />
            <span className="truncate">{r.country || '—'}</span>
          </span>
          {r.city ? (
            <div className="ml-[25px] truncate text-[11.5px] text-text-3">{r.city}</div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'sessionMs',
      label: t('ip.colDuration'),
      align: 'right',
      width: 'w-[64px]',
      sortValue: (r) => r.sessionMs,
      render: (r) => <span className="font-mono text-text-2">{fmtDuration(r.sessionMs)}</span>,
    },
    {
      key: 'clickEvents',
      label: t('ip.colClicks'),
      align: 'right',
      width: 'w-[58px]',
      sortValue: (r) => r.clickEvents,
      render: (r) => (
        <span
          className={`font-mono ${r.clickEvents >= 6 ? 'font-semibold text-suspect-fg' : 'text-text-2'}`}
        >
          {r.clickEvents}
        </span>
      ),
    },
    {
      key: 'recidivismIp',
      label: (
        <span className="inline-flex items-center gap-1">
          {t('ip.colRecidive')}
          <Hint text={t('ip.hintRecidive')} />
        </span>
      ),
      align: 'right',
      width: 'w-[76px]',
      sortValue: (r) => r.recidivismIp,
      render: (r) => (
        <span
          className={`font-mono ${r.recidivismIp >= 10 ? 'font-semibold text-fraud-fg' : r.recidivismIp >= 6 ? 'font-semibold text-suspect-fg' : 'text-text-2'}`}
        >
          {r.recidivismIp}×
        </span>
      ),
    },
    {
      key: 'conv',
      label: t('ip.colConversion'),
      sortable: false,
      width: 'w-[110px]',
      render: conversion,
    },
    {
      key: 'fraudScore',
      label: (
        <span className="inline-flex items-center gap-1">
          {t('ip.colRisk')}
          <Hint text={t('ip.hintRisk')} />
        </span>
      ),
      align: 'left',
      width: 'w-[94px]',
      sortValue: (r) => r.fraudScore,
      render: riskBar,
    },
    {
      key: 'act',
      label: '',
      sortable: false,
      width: 'w-[92px]',
      render: (r) => (
        <Link
          to={`/projects/${id}/visitor/${r.visitId}`}
          className="inline-flex items-center rounded-[4px] bg-[#242426] px-2 py-1 text-[13px] font-semibold text-white transition-colors hover:bg-[#37373a]"
        >
          {t('ip.details')}
        </Link>
      ),
    },
  ];

  const s = summaryQ.data;
  const connData = (s?.connType ?? [])
    .filter((c) => c.visits > 0)
    .map((c) => ({
      label: t(`ip.conn_${c.key}`),
      value: c.visits,
      color: CONN_COLOR[c.key] ?? '#888',
    }));
  const connTotal = connData.reduce((a, b) => a + b.value, 0);
  const nonResid = connData
    .filter((c) => c.label !== t('ip.conn_residential'))
    .reduce((a, b) => a + b.value, 0);
  const repMax = Math.max(1, s ? s.reputation.low + s.reputation.moderate + s.reputation.high : 1);
  const repBars: { label: string; value: number; color: string }[] = s
    ? [
        { label: t('ip.repLow'), value: s.reputation.low, color: '#16a34a' },
        { label: t('ip.repSuspect'), value: s.reputation.moderate, color: '#d97706' },
        { label: t('ip.repHigh'), value: s.reputation.high, color: '#dc2626' },
      ]
    : [];

  return (
    <div>
      <PageHeader
        title={t('ip.title')}
        sub={t('ip.sub')}
        actions={
          <>
            <Btn icon={Download} onClick={exportCsv} disabled={rows.length === 0}>
              {t('ip.export')}
            </Btn>
            <Btn primary icon={ShieldBan} onClick={() => setBl({})}>
              {t('blocklist.add')}
            </Btn>
          </>
        }
      />

      {/* Sober one-line pool-detector summary. The full block (list, copy for Google
          Ads, "replace your list") lives in the Action Center — this page stays clean. */}
      {rangeData && rangeData.ranges.length > 0 ? (
        <Link
          to={`/projects/${id}/ads/action-center`}
          className="mb-4 flex items-center gap-2.5 rounded-xl border border-[#f3c9c9] bg-fraud-bg px-3.5 py-2.5 text-[13px] transition-colors hover:bg-[#fbe3e3]"
        >
          <TriangleAlert className="h-4 w-4 shrink-0 text-fraud-fg" />
          <span className="min-w-0 flex-1">
            <span className="font-semibold text-text">
              {rangeData.summary.rangesFlagged} {t('range.title')}
            </span>
            <span className="text-text-2">
              {' · '}
              {rangeData.summary.adsClicksFlagged} {t('range.adClicks')} {t('range.thisPeriod')}
            </span>
          </span>
          <span className="whitespace-nowrap font-semibold text-fraud-fg">
            {t('range.seeActionCenter')} →
          </span>
        </Link>
      ) : rangeData ? (
        <div className="mb-4 flex items-center gap-2.5 rounded-xl border border-border bg-surface px-3.5 py-2.5 text-[12.5px] text-text-3">
          <ShieldCheck className="h-4 w-4 shrink-0 text-clean-fg" />
          <span>{t('range.none')}</span>
        </div>
      ) : null}

      {/* 3 summary cards */}
      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card>
          <CardHead title={t('ip.connTitle')} hint={t('ip.connHint')} />
          {!s ? (
            <div className="h-40 animate-pulse rounded-lg bg-neutral-bg" />
          ) : connTotal === 0 ? (
            <p className="py-8 text-center text-[12.5px] text-text-3">{t('ip.noData')}</p>
          ) : (
            <div className="flex items-center gap-4">
              <Donut
                data={connData}
                size={150}
                center={{
                  value: `${Math.round((nonResid / connTotal) * 100)} %`,
                  label: t('ip.connNonResid'),
                }}
              />
              <div className="flex flex-1 flex-col gap-1.5">
                {connData.map((d) => (
                  <div key={d.label} className="flex items-center gap-2 text-[12.5px]">
                    <span className="h-2.5 w-2.5 rounded" style={{ background: d.color }} />
                    <span className="flex-1 text-text-2">{d.label}</span>
                    <span className="font-mono text-text-3">
                      {Math.round((d.value / connTotal) * 100)} %
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        <Card>
          <CardHead title={t('ip.repTitle')} sub={t('ip.repCaveat')} />
          {!s ? (
            <div className="h-40 animate-pulse rounded-lg bg-neutral-bg" />
          ) : (
            <>
              <div className="flex flex-col gap-2.5">
                {repBars.map((b) => (
                  <div key={b.label} className="flex items-center gap-2.5 text-[12.5px]">
                    <span className="w-28 text-text-2">{b.label}</span>
                    <span className="h-2 flex-1 overflow-hidden rounded-full bg-neutral-bg">
                      <span
                        className="block h-full rounded-full"
                        style={{ width: `${(b.value / repMax) * 100}%`, background: b.color }}
                      />
                    </span>
                    <span className="w-14 text-right font-mono text-text-3">
                      {b.value.toLocaleString('fr-FR')}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-3.5 flex justify-between border-t border-border pt-3 text-[12.5px]">
                <span className="text-text-2">{t('ip.repUniques')}</span>
                <span className="font-mono font-semibold">
                  {s.reputation.uniqueIps.toLocaleString('fr-FR')}
                </span>
              </div>
            </>
          )}
        </Card>

        <Card>
          <CardHead
            title={t('ip.blTitle')}
            right={
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#f6821f]">
                <Cloud className="h-3.5 w-3.5" />
                Cloudflare
              </span>
            }
          />
          {!s ? (
            <div className="h-40 animate-pulse rounded-lg bg-neutral-bg" />
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-[30px] font-bold text-fraud-fg">
                  {(s.blocklist.total + s.blocklist.ranges).toLocaleString('fr-FR')}
                </span>
                <span className="text-[13px] text-text-2">{t('ip.blActiveEntities')}</span>
              </div>
              <div className="text-[12px] text-text-3">
                {`${t('units.individualIp', { count: s.blocklist.total })} · ${t('units.range', { count: s.blocklist.ranges })}`}
              </div>
              {(
                [
                  ['blDatacenter', s.blocklist.datacenter, 'suspect'],
                  ['blVpnProxy', s.blocklist.vpnProxy, 'info'],
                  ['blTor', s.blocklist.tor, 'neutral'],
                  ['blManual', s.blocklist.manual, 'blue'],
                ] as const
              ).map(([k, v, tone]) => (
                <div key={k} className="flex items-center justify-between text-[13px]">
                  <span className="text-text-2">{t(`ip.${k}`)}</span>
                  <Badge tone={tone}>{v}</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Recent visits */}
      <Card className="p-0">
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3.5">
          <div className="text-[14.5px] font-semibold">{t('ip.recent')}</div>
          <span className="text-[12px] text-text-3">
            {rows.length} {t('ip.sessions')}
          </span>
          <div className="flex-1" />
          <input
            value={ipDraft}
            onChange={(e) => setIpDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setIpSearch(ipDraft.trim());
            }}
            onBlur={() => setIpSearch(ipDraft.trim())}
            placeholder={t('ip.searchIp')}
            spellCheck={false}
            className="h-8 w-[210px] rounded-lg border border-border bg-surface px-2.5 font-mono text-[12px] text-text outline-none focus:border-primary"
          />
          {FILTERS.map((f) => (
            <Chip key={f.key} active={filter === f.key} onClick={() => setFilter(f.key)}>
              {t(`ip.${f.label}`)}
            </Chip>
          ))}
        </div>
        {visitsQ.isLoading ? (
          <div className="h-48 animate-pulse bg-neutral-bg" />
        ) : rows.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <p className="text-[13px] font-semibold text-text-2">{t('ip.emptyTitle')}</p>
            <p className="mt-1 text-[12.5px] text-text-3">{t('ip.emptyBody')}</p>
          </div>
        ) : (
          <>
            <DataTable
              // Remount on period / filter / search change so the default sort
              // (newest first) is re-applied; manual column sorting stays on top.
              key={`${range.period}:${range.from ?? ''}:${range.to ?? ''}:${filter}:${ipSearch}`}
              columns={visitCols}
              rows={rows}
              rowKey={(r) => r.visitId}
              initialSort={{ key: 'arrivedAt', dir: 'desc' }}
              dense
            />
            {visitsQ.hasNextPage ? (
              <div className="flex justify-center p-3.5">
                <Btn disabled={visitsQ.isFetchingNextPage} onClick={() => visitsQ.fetchNextPage()}>
                  {visitsQ.isFetchingNextPage ? '…' : t('overview.viewAll')}
                </Btn>
              </div>
            ) : null}
          </>
        )}
      </Card>

      <div className="mt-4">
        <RuleAdoptionPanel projectId={id} />
      </div>

      {bl ? <BlocklistModal projectId={id} prefill={bl} onClose={() => setBl(null)} /> : null}
    </div>
  );
}
